import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

/* ── Small SVG helpers ── */
const AppleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.3.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M3.18 23.4c.3.16.63.19.96.07l13.5-7.79L3 7.8 2.82 21.7c-.01.67.12 1.36.36 1.7zm1.32-17.7L17.46 13 4.5 5.4V5.7zm14.1 6.9l-2.58-1.49-2.58 1.49 2.58 4.47 2.58-4.47z" />
  </svg>
);

const MapPinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const ChatIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const UsersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const PollIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const features = [
  { icon: <MapPinIcon />, title: 'Explore Nearby Flares', desc: 'Discover what\'s happening around you on an interactive map. Join flares, meet people nearby, and never miss out on the fun.' },
  { icon: <ChatIcon />, title: 'Real-time Group Chats', desc: 'Every flare gets its own group chat so plans come together fast. Coordinate, share media, and build excitement instantly.' },
  { icon: <UsersIcon />, title: 'Friends & Connections', desc: 'Build your circle. Categorize people as friends or acquaintances, create groups, and keep your social life organized.' },
  { icon: <PollIcon />, title: 'Quick Polls', desc: 'Settle the details instantly — "Where to eat?" "What time to meet?" One tap and everyone votes. No more endless back-and-forth.' },
  { icon: <MapPinIcon />, title: 'Create Your Flare', desc: 'Shoot up a flare for any occasion. Happy hour, golf weekend, pizza night — set privacy controls and invite exactly who you want.' },
  { icon: <ShieldIcon />, title: 'Privacy Controls', desc: 'You decide who sees each flare. Share publicly in the area, with connections only, or keep it private for a select group.' },
];

/* ── Intersection Observer hook ── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('revealed');
          obs.unobserve(e.target);
        }
      }),
      { threshold: 0.12 }
    );
    const items = ref.current.querySelectorAll('.reveal');
    items.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
  return ref;
}

export default function Home() {
  const featRef = useReveal();
  const screenRef = useReveal();
  const howRef = useReveal();
  const ctaRef = useReveal();

  return (
    <div className="home">
      {/* ── HERO ── */}
      <section className="hero">
        {/* Background orbs */}
        <div className="hero-orb orb-1" />
        <div className="hero-orb orb-2" />
        <div className="hero-orb orb-3" />

        <div className="hero-content container">
          <div className="hero-text">
            <div className="section-tag animate-fade-up">
              <span className="dot" />
              Now Available on iOS & Android
            </div>

            <h1 className="hero-heading animate-fade-up delay-1">
              Stop Scrolling,<br />
              <span className="gradient-text">Start Living.</span>
            </h1>

            <p className="hero-sub animate-fade-up delay-2">
              FlareChat connects you with the people who matter most — in real life.
              Shoot up a flare, see who's free, and make plans that actually happen.
            </p>

            <div className="hero-stores animate-fade-up delay-3">
              <a href="https://apps.apple.com" target="_blank" rel="noopener noreferrer" className="store-badge">
                <AppleIcon />
                <span className="store-badge-text">
                  <span>Download on the</span>
                  <span>App Store</span>
                </span>
              </a>
              <a href="https://play.google.com" target="_blank" rel="noopener noreferrer" className="store-badge">
                <PlayIcon />
                <span className="store-badge-text">
                  <span>Get it on</span>
                  <span>Google Play</span>
                </span>
              </a>
            </div>

            <div className="hero-stats animate-fade-up delay-4">
              <div className="stat">
                <strong>IRL</strong>
                <span>Focus</span>
              </div>
              <div className="stat-divider" />
              <div className="stat">
                <strong>Real-time</strong>
                <span>Group Chats</span>
              </div>
              <div className="stat-divider" />
              <div className="stat">
                <strong>Zero</strong>
                <span>Endless Scroll</span>
              </div>
            </div>
          </div>

          <div className="hero-phones animate-fade-in delay-3">
            <div className="phone-main">
              <img src="/images/screen3.jpg" alt="FlareChat Home Feed" loading="eager" />
            </div>
            <div className="phone-secondary">
              <img src="/images/screen1.jpg" alt="FlareChat Map View" loading="eager" />
            </div>
          </div>
        </div>

        <div className="hero-wave">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none">
            <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="#F7F7FC" />
          </svg>
        </div>
      </section>

      {/* ── SOCIAL PROOF STRIP ── */}
      <section className="proof-strip">
        <div className="container">
          <p className="proof-text">The app built for <em>real connections</em>, not endless feeds</p>
          <div className="proof-badges">
            <span>📍 Location-based</span>
            <span>💬 Group Chats</span>
            <span>📊 Quick Polls</span>
            <span>🔒 Privacy Controls</span>
            <span>🎯 Zero Algorithms</span>
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="features" ref={featRef}>
        <div className="container">
          <div className="section-header reveal">
            <div className="section-tag">
              <span className="dot" />
              Everything you need
            </div>
            <h2>Built for Real-World<br /><span className="gradient-text">Connection</span></h2>
            <p>Every feature is designed around one goal — helping you spend more time together offline.</p>
          </div>

          <div className="features-grid">
            {features.map((f, i) => (
              <div key={i} className={`feature-card reveal`} style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="feature-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SCREENSHOT SHOWCASE ── */}
      <section className="screenshots" ref={screenRef}>
        <div className="screenshots-bg" />
        <div className="container">
          <div className="section-header reveal" style={{ color: 'white' }}>
            <div className="section-tag" style={{ background: 'rgba(245,166,35,0.2)', color: '#F5A623', borderColor: 'rgba(245,166,35,0.3)' }}>
              <span className="dot" style={{ background: '#F5A623' }} />
              App Screens
            </div>
            <h2 style={{ color: 'white' }}>See FlareChat<br /><span className="gradient-text">In Action</span></h2>
          </div>

          <div className="screens-row">
            {[
              { src: '/images/screen3.jpg', label: 'Your Flares Feed' },
              { src: '/images/screen5.jpg', label: 'Chat with Friends' },
              { src: '/images/screen6.jpg', label: 'Connections' },
              { src: '/images/screen7.jpg', label: 'Your Profile' },
            ].map((s, i) => (
              <div key={i} className={`screen-item reveal`} style={{ animationDelay: `${i * 0.12}s` }}>
                <div className="screen-frame">
                  <img src={s.src} alt={s.label} loading="lazy" />
                </div>
                <p className="screen-label">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="how-it-works" ref={howRef}>
        <div className="container">
          <div className="section-header reveal">
            <div className="section-tag">
              <span className="dot" />
              Simple as 1-2-3
            </div>
            <h2>How FlareChat<br /><span className="gradient-text">Works</span></h2>
          </div>

          <div className="how-inner">
            <div className="steps">
              {[
                { num: '01', title: 'Shoot a Flare', desc: "Post what you're up to — happy hour, a hike, a movie night, anything." },
                { num: '02', title: "See Who's Free", desc: 'Nearby friends and connections see your flare on the map and in their feed. They join in with one tap.' },
                { num: '03', title: 'Hang Out IRL', desc: 'Chat in the flare group, coordinate the details with polls, and go have a great time together.' },
              ].map((step, i) => (
                <div key={i} className="step reveal" style={{ animationDelay: `${i * 0.15}s` }}>
                  <div className="step-num">{step.num}</div>
                  <div className="step-content">
                    <h3>{step.title}</h3>
                    <p>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="how-visual reveal delay-5">
              <img src="/images/screen1.jpg" alt="Explore Map" className="how-phone" />
              <div className="how-card card-1">
                <span className="card-emoji">🔥</span>
                <div>
                  <strong>Happy Hour?</strong>
                  <p>Alex.029 · 0.1 mi</p>
                </div>
              </div>
              <div className="how-card card-2">
                <span className="card-emoji">⛳</span>
                <div>
                  <strong>Golf this weekend</strong>
                  <p>Dan0123 · 2.3 mi</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cta-section" ref={ctaRef}>
        <div className="cta-orb cta-orb-1" />
        <div className="cta-orb cta-orb-2" />
        <div className="container">
          <div className="cta-inner reveal">
            <div className="cta-flame">🔥</div>
            <h2>Don't get left out<br />of the fun.</h2>
            <p>See who's free and join in with FlareChat. Available on iOS and Android, completely free.</p>
            <div className="cta-stores">
              <a href="https://apps.apple.com" target="_blank" rel="noopener noreferrer" className="store-badge">
                <AppleIcon />
                <span className="store-badge-text">
                  <span>Download on the</span>
                  <span>App Store</span>
                </span>
              </a>
              <a href="https://play.google.com" target="_blank" rel="noopener noreferrer" className="store-badge">
                <PlayIcon />
                <span className="store-badge-text">
                  <span>Get it on</span>
                  <span>Google Play</span>
                </span>
              </a>
            </div>
            <p className="cta-legal">Also Available on Google Play Store</p>
          </div>
        </div>
      </section>
    </div>
  );
}
